import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AppShell } from "../components/layout/AppShell";
import { GPTIcon } from "../components/GPTIcon";
import { useAuth, isAdminRole } from "../context/AuthContext";
import { Lock, ArrowUpRight, Loader2, Search, SlidersHorizontal, X, Calculator } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATIC_FILTERS = ["All", "Strategy", "Marketing", "Operations", "Automation"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gpts, setGpts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [program, setProgram] = useState("action_program"); // "action_program" | "mentorship"
  const [tagFilter, setTagFilter] = useState("all"); // mentorship-tab pill: "all" | "shopify"
  const [showMentorshipLockedModal, setShowMentorshipLockedModal] = useState(false);
  const isAdmin = isAdminRole(user?.role);
  const hasMentorshipAccess = isAdmin || !!user?.has_mentorship_access;

  // Close the locked-mentorship modal when the user hits Escape (a11y parity
  // with the backdrop / close-X dismiss paths).
  useEffect(() => {
    if (!showMentorshipLockedModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowMentorshipLockedModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showMentorshipLockedModal]);

  useEffect(() => {
    api
      .get("/gpts")
      .then(({ data }) => setGpts(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load GPTs"))
      .finally(() => setLoading(false));
  }, []);

  // Reset the tag pill whenever the user switches program tabs.
  useEffect(() => {
    setTagFilter("all");
  }, [program]);

  const startChat = (gpt) => {
    // Users can browse mentorship GPTs but cannot open them unless they have
    // been granted mentorship access (admin/master_admin get it automatically,
    // regular users need an explicit grant). Show the premium locked modal
    // otherwise. (This check must run BEFORE the tool-routing slug-gates
    // below so mentorship tools like Funnel Audit AI also respect the lock.)
    const cat = gpt?.category || "action_program";
    if (cat === "mentorship" && !hasMentorshipAccess) {
      setShowMentorshipLockedModal(true);
      return;
    }
    // Funnel Audit AI is a vision-based tool, not a chat. Route directly to
    // its dedicated upload page.
    if (gpt?.slug === "funnel-audit-ai") {
      navigate("/tools/funnel-audit");
      return;
    }
    // P&L Calculator is a dedicated tool page (CSV math, not LLM chat).
    if (gpt?.slug === "pnl-calculator") {
      navigate("/tools/pnl-calculator");
      return;
    }
    if (gpt.locked) {
      toast.error("This GPT is locked by admin");
      return;
    }
    setStarting(gpt.id);
    navigate(`/chat/${gpt.id}`, { state: { gpt } });
  };

  // Stats (across all programs the user has access to)
  // Defensive: coerce to array — production has historically returned an
  // HTML error page when the backend ingress is misrouted, which would
  // poison .filter() and crash the whole page.
  const safeGpts = Array.isArray(gpts) ? gpts : [];
  const totalAgents = safeGpts.length;
  const unlockedAgents = safeGpts.filter((g) => !g.locked).length;
  const planLabel = user?.role === "master_admin" ? "Master Admin" : (isAdmin ? "Admin" : "Operator");

  // Filtered list — by program tab + tag pill + search query
  const filteredGpts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = Array.isArray(gpts) ? gpts : [];
    return source.filter((g) => {
      const cat = g.category || "action_program";
      if (cat !== program) return false;
      // Tag pill applies only on the mentorship tab right now
      if (program === "mentorship" && tagFilter !== "all") {
        const tags = g.tags || [];
        if (!tags.includes(tagFilter)) return false;
      }
      if (q && !(g.name.toLowerCase().includes(q) || g.description.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [gpts, search, program, tagFilter]);

  const dayLabel = new Date()
    .toLocaleDateString(undefined, { weekday: "long" })
    .toUpperCase();

  return (
    <AppShell>
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Hero + Stats grid */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* Hero panel */}
          <div
            data-testid="hero-panel"
            className="lg:col-span-8 relative overflow-hidden bg-card border border-border/60 rounded-3xl p-6 md:p-10 shadow-subtle"
          >
            <div
              aria-hidden
              className="absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-30"
              style={{
                background:
                  "radial-gradient(circle, hsl(var(--secondary)) 0%, transparent 70%)",
              }}
            />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                // GPT HUB / {dayLabel}
              </div>
              <h1
                data-testid="dashboard-title"
                className="mt-3 font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
              >
                Good to see you,{" "}
                <span className="text-brand underline decoration-brand/60 decoration-2 underline-offset-[6px]">
                  {user?.name?.split(" ")[0] || "Operator"}
                </span>
                .
              </h1>
              <p className="text-muted-foreground text-sm md:text-base mt-4 max-w-lg leading-relaxed">
                Pick an agent to begin. Built for ecommerce operators — from
                first store to scaling brands. Each one is hand-prompted for a
                single craft.
              </p>
            </div>
          </div>

          {/* Stats grid — Unlocked / Total Agents / Plan */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-4 md:gap-6">
            <StatCard label="Unlocked" value={unlockedAgents} testId="stat-unlocked" />
            <StatCard label="Total Agents" value={totalAgents} testId="stat-total" />
            <div className="col-span-2">
              <StatCard label="Plan" value={planLabel} small testId="stat-plan" />
            </div>
          </div>
        </section>

        {/* Program tabs — Action Program vs 6 Months Mentorship */}
        <section className="mt-8 md:mt-10" data-testid="program-tabs">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Programs
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "action_program", label: "Action Program" },
              { key: "mentorship", label: "6 Months Mentorship" },
            ].map((p) => {
              const active = program === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setProgram(p.key)}
                  data-testid={`program-tab-${p.key}`}
                  className={[
                    "h-11 px-5 md:px-6 rounded-full text-xs md:text-[13px] uppercase tracking-[0.18em] font-bold transition-all border-[1.5px]",
                    active
                      ? "bg-[#b89846] text-black border-[#b89846] shadow-[0_8px_22px_-8px_rgba(184,152,70,0.55)]"
                      : "border-[#b89846]/35 text-[#b89846] hover:bg-[#b89846]/10 hover:border-[#b89846]/60",
                  ].join(" ")}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Mentorship-only sub-filter pills (tags) */}
          {program === "mentorship" && (
            <div
              className="mt-4 flex flex-wrap gap-2"
              data-testid="mentorship-tag-pills"
            >
              {[
                { key: "all", label: "All" },
                { key: "shopify", label: "Shopify" },
                { key: "meta", label: "Meta" },
                { key: "roas", label: "ROAS" },
                { key: "shipping", label: "Shipping" },
                { key: "whatsapp", label: "WhatsApp" },
                { key: "email", label: "Email" },
              ].map((t) => {
                const active = tagFilter === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTagFilter(t.key)}
                    data-testid={`mentorship-tag-${t.key}`}
                    className={[
                      "h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold transition-all border",
                      active
                        ? "bg-white text-black border-white"
                        : "border-white/15 text-white/70 hover:bg-white/5 hover:text-white",
                    ].join(" ")}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Search + filters */}
        <section className="mt-6 md:mt-8 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 md:max-w-sm">
            <Search
              size={16}
              strokeWidth={1.6}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              data-testid="dashboard-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents..."
              className="w-full h-12 rounded-full bg-card border border-border/60 pl-11 pr-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-foreground/10 transition"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto thin-scroll pb-1 -mx-4 px-4 md:mx-0 md:px-0">
            <button
              type="button"
              data-testid="filter-icon"
              className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full border border-border/60 hover:bg-secondary transition-colors"
              aria-label="Filters"
            >
              <SlidersHorizontal size={15} strokeWidth={1.6} />
            </button>
            {STATIC_FILTERS.map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f.toLowerCase()}`}
                  className={[
                    "h-10 shrink-0 px-4 md:px-5 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold transition-colors border",
                    active
                      ? "bg-brand text-brand-foreground border-brand"
                      : "border-border/60 text-muted-foreground hover:bg-secondary hover:text-foreground",
                  ].join(" ")}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </section>

        {/* GPT cards grid */}
        <section className="mt-6 md:mt-8" data-testid="gpt-grid">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-72 rounded-3xl bg-secondary/60 animate-pulse"
                />
              ))}
            </div>
          ) : filteredGpts.length === 0 ? (
            <div
              className="text-center py-16 md:py-20 border border-dashed border-[#b89846]/30 rounded-3xl bg-[#0F0F0E]"
              data-testid="empty-state"
            >
              {program === "mentorship" && tagFilter !== "all" ? (
                <div className="text-muted-foreground">
                  No mentorship agents tagged{" "}
                  <span className="text-white font-medium uppercase tracking-wider">
                    {tagFilter}
                  </span>{" "}
                  yet.
                </div>
              ) : program === "mentorship" ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#b89846]/80 font-bold">
                    Coming soon
                  </div>
                  <h3 className="mt-3 font-heading text-2xl md:text-3xl tracking-tight text-white">
                    6 Months Mentorship
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-white/65 max-w-md mx-auto leading-relaxed">
                    Dedicated mentorship agents and resources for this program
                    are being curated. Check back shortly.
                  </p>
                </>
              ) : search.trim() ? (
                <div className="text-muted-foreground">No agents match your search.</div>
              ) : (
                <div className="text-muted-foreground">
                  No agents have been granted to you in this program yet. Please contact the admin.
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredGpts.map((gpt, idx) => (
                <GPTCard
                  key={gpt.id}
                  gpt={gpt}
                  index={idx}
                  starting={starting === gpt.id}
                  onOpen={() => startChat(gpt)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Mentorship locked modal — premium centered card */}
      {showMentorshipLockedModal ? (
        <div
          data-testid="mentorship-locked-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mentorship-locked-title"
          onClick={() => setShowMentorshipLockedModal(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/75 backdrop-blur-sm animate-in fade-in duration-200"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl overflow-hidden bg-[#0F0F0E] border border-[#b89846]/40 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9),0_0_0_1px_rgba(184,152,70,0.15)] animate-in zoom-in-95 fade-in duration-200"
          >
            {/* Subtle gold gradient halo at the top */}
            <div
              aria-hidden
              className="absolute -top-32 left-1/2 -translate-x-1/2 w-[480px] h-[260px] rounded-full blur-3xl opacity-25 pointer-events-none"
              style={{ background: "radial-gradient(closest-side, #b89846, transparent)" }}
            />

            {/* Close */}
            <button
              type="button"
              onClick={() => setShowMentorshipLockedModal(false)}
              data-testid="mentorship-locked-close-btn"
              aria-label="Close"
              className="absolute top-4 right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#b89846]/30 text-[#b89846]/80 hover:text-black hover:bg-[#b89846] hover:border-[#b89846] transition"
            >
              <X size={16} strokeWidth={2.2} />
            </button>

            <div className="relative px-7 pt-10 pb-8 text-center">
              {/* Lock badge */}
              <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#b89846]/45 bg-gradient-to-b from-[#1a1815] to-[#0a0a09] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-18px_rgba(184,152,70,0.45)]">
                <Lock size={26} strokeWidth={1.8} className="text-[#e8c879]" />
              </div>

              <div
                data-testid="mentorship-locked-eyebrow"
                className="text-[10px] uppercase tracking-[0.32em] text-[#b89846]/80 font-bold mb-2"
              >
                6 Months Mentorship
              </div>
              <h2
                id="mentorship-locked-title"
                className="text-2xl md:text-[26px] font-semibold text-white leading-tight tracking-tight mb-3"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, system-ui, sans-serif" }}
              >
                This GPT is currently locked
              </h2>
              <p
                data-testid="mentorship-locked-body"
                className="text-[14px] md:text-[15px] text-white/70 leading-relaxed mb-7 max-w-sm mx-auto"
              >
                The 6 Months Mentorship is currently locked. To unlock access, please contact your admin.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="mailto:shreyas@allianceoftitans.com?subject=6%20Months%20Mentorship%20Access%20Request"
                  data-testid="mentorship-locked-contact-btn"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-[12px] uppercase tracking-[0.18em] font-bold transition-all"
                  style={{
                    background:
                      "linear-gradient(180deg, #e1c074 0%, #b89846 55%, #8a6f2a 100%)",
                    color: "#0b0b0a",
                    boxShadow:
                      "0 12px 28px -10px rgba(184,152,70,0.6), inset 0 1px 0 rgba(255,255,255,0.45)",
                  }}
                >
                  Contact Admin
                  <ArrowUpRight size={14} strokeWidth={2.4} />
                </a>
                <button
                  type="button"
                  onClick={() => setShowMentorshipLockedModal(false)}
                  data-testid="mentorship-locked-dismiss-btn"
                  className="inline-flex items-center justify-center h-11 px-5 rounded-full text-[12px] uppercase tracking-[0.18em] font-bold text-[#b89846] border-[1.5px] border-[#b89846]/35 hover:bg-[#b89846]/10 hover:border-[#b89846]/60 transition"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
}

function StatCard({ label, value, small = false, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative bg-card border border-border/60 rounded-2xl md:rounded-3xl p-4 md:p-5 shadow-subtle overflow-hidden flex flex-col justify-between min-h-[90px] md:min-h-[110px]"
    >
      <div className="text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      <div
        className={[
          "font-heading font-bold tracking-tighter tabular-nums mt-2",
          small ? "text-xl md:text-2xl" : "text-3xl md:text-4xl",
        ].join(" ")}
      >
        {value}
      </div>
    </div>
  );
}

function GPTCard({ gpt, index, starting, onOpen }) {
  const locked = gpt.locked;
  // Mentorship-category GPTs are visible to non-admins but kept clickable so
  // the click can pop the "contact admin to unlock" modal. Admin-locked GPTs
  // (category !== mentorship) stay truly disabled.
  const isMentorshipLocked = locked && gpt.category === "mentorship";
  return (
    <button
      data-testid={`gpt-card-${gpt.slug}`}
      onClick={onOpen}
      disabled={(locked && !isMentorshipLocked) || starting}
      style={{
        animationDelay: `${index * 50}ms`,
        // Premium dark card — deep black base with a subtle warm-charcoal lift
        // toward the bottom and an antique-gold radial gloss in the top-right
        // corner that mimics light catching brushed metal. Mirrors the SK
        // Kapoor logo palette: black body, gold accents, white type.
        backgroundImage:
          "radial-gradient(120% 90% at 100% 0%, rgba(184,152,70,0.22) 0%, rgba(184,152,70,0) 45%), linear-gradient(160deg, #141210 0%, #0A0908 45%, #050403 100%)",
      }}
      className={[
        "group relative text-left text-white rounded-3xl p-5 md:p-6 overflow-hidden",
        "border-[1.5px] border-[#b89846]/55 ring-1 ring-inset ring-[#b89846]/15",
        "shadow-[0_18px_38px_-18px_rgba(0,0,0,0.85),0_1px_0_0_rgba(184,152,70,0.35)_inset]",
        "flex flex-col min-h-[260px] md:min-h-[280px]",
        "transition-all duration-300",
        locked
          ? isMentorshipLocked
            ? "opacity-80 cursor-pointer hover:opacity-100 hover:border-[#d9b35c]/80 hover:shadow-[0_22px_42px_-18px_rgba(184,152,70,0.35)]"
            : "opacity-60 cursor-not-allowed"
          : "hover:-translate-y-1 hover:border-[#d9b35c] hover:shadow-[0_28px_50px_-18px_rgba(184,152,70,0.45),0_1px_0_0_rgba(184,152,70,0.5)_inset]",
      ].join(" ")}
    >
      {/* Top hairline gloss — antique-gold highlight that mimics polished metal */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#d9b35c] to-transparent opacity-90"
      />
      {/* Soft warm under-glow in bottom-left for depth */}
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full opacity-[0.18]"
        style={{
          background:
            "radial-gradient(circle, rgba(184,152,70,0.55) 0%, rgba(184,152,70,0) 60%)",
        }}
      />

      {/* Top row: icon + access badge */}
      <div className="relative flex items-start justify-between gap-3">
        <div
          className="h-11 w-11 md:h-12 md:w-12 rounded-2xl flex items-center justify-center text-[#e8c879] shadow-[0_1px_0_0_rgba(184,152,70,0.35)_inset]"
          style={{
            background:
              "linear-gradient(160deg, rgba(184,152,70,0.16) 0%, rgba(184,152,70,0.06) 100%)",
            border: "1px solid rgba(184,152,70,0.30)",
          }}
        >
          <GPTIcon name={gpt.icon} size={20} />
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {gpt.access_type && (
            <span
              data-testid={`gpt-access-${gpt.slug}`}
              className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold text-black shadow-[0_1px_0_0_rgba(255,255,255,0.35)_inset]"
              style={{
                background:
                  "linear-gradient(180deg, #d9b35c 0%, #b89846 60%, #8a6f2a 100%)",
              }}
            >
              {gpt.access_type}
            </span>
          )}
          <span
            data-testid={`gpt-status-${gpt.slug}`}
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#b89846]/75"
          >
            {locked ? (
              <span className="inline-flex items-center gap-1 text-rose-300">
                <Lock size={10} strokeWidth={2} /> Locked
              </span>
            ) : (
              "Custom GPT"
            )}
          </span>
        </div>
      </div>

      {/* Title + description */}
      <div className="relative mt-6 flex-1">
        <h3 className="font-heading text-lg md:text-xl font-semibold tracking-tight leading-snug text-white">
          {gpt.name}
        </h3>
        <p className="mt-2 text-sm text-white/65 leading-relaxed line-clamp-3">
          {gpt.description}
        </p>
      </div>

      {/* Footer divider */}
      <div className="relative mt-4 pt-4 border-t border-[#b89846]/18 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#b89846]/65 font-medium">
          Agent
        </span>
        <span
          className={[
            "inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold transition text-black",
            locked
              ? "bg-white/10 text-white/45"
              : "group-hover:scale-[1.04] shadow-[0_8px_20px_-6px_rgba(184,152,70,0.55)]",
          ].join(" ")}
          style={
            locked
              ? undefined
              : {
                  background:
                    "linear-gradient(180deg, #e1c074 0%, #b89846 55%, #8a6f2a 100%)",
                  boxShadow:
                    "0 8px 20px -6px rgba(184,152,70,0.55), inset 0 1px 0 rgba(255,255,255,0.45)",
                }
          }
        >
          {starting ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <>
              Open <ArrowUpRight size={13} strokeWidth={2.2} />
            </>
          )}
        </span>
      </div>
    </button>
  );
}
